import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Card,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { EmptyState, LicensePlate, StatusChip, TableSkeleton } from '../../../components/ui';
import { Appointment, AppointmentStatus } from '../../../types';
import { shopWallClockDate } from '../../../utils/shopTime';
import { vehicleOf } from './helpers';

interface TodayAppointmentsProps {
  appointments: Appointment[];
  loading: boolean;
  timeZone?: string;
}

const COLUMNS = ['Time', 'Customer', 'Vehicle', 'Service', 'Status'];

/** Today's bookings on the shop's clock, cancelled ones left out. */
const TodayAppointments: React.FC<TodayAppointmentsProps> = ({ appointments, loading, timeZone }) => {
  const rows = appointments.filter((job) => job.status !== AppointmentStatus.CANCELLED);

  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 2.5, pt: 2.25, pb: 2 }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontSize: '1rem' }}>
            Today&apos;s appointments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length === 1 ? '1 booked' : `${rows.length} booked`}
          </Typography>
        </Box>
        <Link
          component={RouterLink}
          to="/admin/appointments"
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, flexShrink: 0 }}
        >
          View all
          <ArrowForwardIcon sx={{ fontSize: 15 }} />
        </Link>
      </Stack>

      {/* Wide content scrolls inside its own container — never the page. */}
      <TableContainer sx={{ border: 0, borderRadius: 0, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableSkeleton rows={4} columns={COLUMNS.length} />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ borderBottom: 'none' }}>
                  <EmptyState dense title="Nothing booked for today" />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {job.appointmentDateTime
                      ? format(shopWallClockDate(job.appointmentDateTime, timeZone), 'h:mm a')
                      : job.appointmentTime}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {job.customerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.customerPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack alignItems="flex-start" spacing={0.375}>
                      <Typography variant="body2">{vehicleOf(job)}</Typography>
                      {job.licensePlate && <LicensePlate plate={job.licensePlate} />}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 220 }}>
                    <Typography variant="body2" color="inherit" noWrap>
                      {job.serviceDescription}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={job.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default TodayAppointments;
